<!--
  ~ Copyright (C) 2025 - present by Potentially
  ~
  ~ Please see distribution for license.
  -->

@let currentReport = report();
@if (currentReport && reportSections().length) {
  <div class="playlist-print-report">
    <!-- Header -->
    <div class="print-report-header">
      <div class="print-report-header-row">
        <div class="print-report-header-text">
          <h1 class="print-report-title extra-large">
            {{
              'translations.playlistPdfReviewTitle'
                | transloco
                  : {
                      playlistTitle: playlistTitle(),
                      date: currentTime | dateFormat: 'DD MMMM yyyy',
                    }
            }}
          </h1>
          <div class="print-report-meta">
            @if (currentReport.userName) {
              <div class="large">
                {{ currentReport.userName }}
              </div>
            }
            <div class="medium">
              {{ 'translations.exportedOn' | transloco: { date: currentTime | dateFormat: 'DD MMMM yyyy' } }}
            </div>
          </div>
        </div>
        @if (playlistThumbnail()) {
          <img [src]="playlistThumbnail()" alt="" class="print-report-thumbnail" />
        }
      </div>
    </div>

    <!-- Table of contents -->
    @let currentAllCards = allCards();
    @if (currentAllCards.length) {
      <div class="print-report-toc">
        <h2 class="print-report-toc-title large bold">{{ 'translations.tableOfContents' | transloco }}</h2>
        <ol class="print-report-toc-list">
          @for (item of currentAllCards; track $index) {
            @if (hasAnyContent(item)) {
              <li>
                @if (supportsPdfAnchors) {
                  <a [href]="'#print-card-' + $index" class="print-report-toc-link">{{ item.title }}</a>
                } @else {
                  <span>{{ item.title }}</span>
                }
              </li>
            }
          }
        </ol>
      </div>
    }

    <!-- Sections -->
    @let currentSections = reportSections();
    @for (section of currentSections; track $index) {
      <div class="print-report-section">
        @if (!section.isMainSection && (section.title || section.segments.length)) {
          <!-- Non-main section: frame title + content together in a block -->
          <div class="print-report-section-block">
            @if (section.title) {
              <div class="print-report-section-header">
                <h2 class="print-report-section-title large bold">{{ section.title }}</h2>
              </div>
            }
            @if (section.segments.length) {
              <div class="print-report-playlist-content">
                @for (segment of section.segments; track $index) {
                  <ng-container *ngTemplateOutlet="segmentTemplate; context: { $implicit: segment }"></ng-container>
                }
              </div>
            }
          </div>
        } @else if (section.isMainSection && section.segments.length) {
          <!-- Main section: content without block frame -->
          <div class="print-report-playlist-content">
            @for (segment of section.segments; track $index) {
              <ng-container *ngTemplateOutlet="segmentTemplate; context: { $implicit: segment }"></ng-container>
            }
          </div>
        }

        <!-- Section cards -->
        @for (item of section.cards; track $index) {
          @if (hasAnyContent(item)) {
            <div class="print-report-card" [id]="'print-card-' + getGlobalCardIndex(item)">
              <!-- Card header -->
              <div class="print-report-card-header">
                <h2 class="print-report-card-title large bold">{{ item.title }}</h2>
                @if (item.thumbnail) {
                  <img [src]="item.thumbnail" alt="" class="print-report-thumbnail" />
                }
              </div>

              <!-- Content segments (interleaved content, documents, and forms) -->
              @for (segment of item.segments; track $index) {
                <ng-container *ngTemplateOutlet="segmentTemplate; context: { $implicit: segment }"></ng-container>
              }

              <!-- Assessment report sections -->
              <ng-container *ngIf="item.assessmentSections as sections">
                <div class="print-report-assessment" *ngIf="sections.length">
                  <ptl-diagnostics-report-conditional-section *ngFor="let section of sections" [section]="section">
                  </ptl-diagnostics-report-conditional-section>
                </div>
              </ng-container>
            </div>
          }
        }
      </div>
    }
  </div>
}

<!-- Reusable segment template -->
<ng-template #segmentTemplate let-data>
  @let segment = castContentSegment(data);
  @switch (segment.type) {
    @case ('content') {
      @if (segment.permalink) {
        <div class="print-report-embed-frame">
          <div class="print-report-permalink">
            <a [href]="segment.permalink" target="_blank">{{ segment.permalinkLabel || segment.permalink }}</a>
          </div>
          <ptl-resource-preview [previewContent]="segment.content"></ptl-resource-preview>
        </div>
      } @else {
        <div class="print-report-content">
          <ptl-resource-preview [previewContent]="segment.content"></ptl-resource-preview>
        </div>
      }
    }
    @case ('document') {
      <div class="print-report-embed-frame">
        @if (segment.documentPermalink) {
          <div class="print-report-permalink">
            <a [href]="segment.documentPermalink" target="_blank">{{ segment.documentPermalinkLabel || segment.documentPermalink }}</a>
          </div>
        }
        @if (segment.documentUrl) {
          <ptl-pdf-pages-renderer [pdfUrl]="segment.documentUrl" (loaded)="onPdfRendererLoaded()"> </ptl-pdf-pages-renderer>
        }
      </div>
    }
    @case ('permalink') {
      <div class="print-report-permalink">
        <a [href]="segment.permalink" target="_blank">{{ segment.permalinkLabel || segment.permalink }}</a>
      </div>
    }
    @case ('external-link') {
      <div class="print-report-embed-frame">
        @if (segment.externalLabel && segment.externalLabel !== segment.externalUrl) {
          <div class="print-report-external-link-label bold">{{ segment.externalLabel }}</div>
        }
        <div class="print-report-permalink">
          <a [href]="segment.externalUrl" target="_blank">{{ segment.externalUrl }}</a>
        </div>
        @if (segment.externalMediaType === 'image') {
          <img [src]="segment.externalUrl" [alt]="segment.externalLabel" class="print-report-external-image" />
        }
        @if (segment.externalMediaType === 'pdf') {
          <ptl-pdf-pages-renderer [pdfUrl]="segment.externalUrl" (loaded)="onPdfRendererLoaded()"> </ptl-pdf-pages-renderer>
        }
      </div>
    }
    @case ('weblink') {
      <div class="print-report-embed-frame">
        <div class="print-report-external-link-label bold">{{ segment.weblinkTitle }}</div>
        <div class="print-report-permalink">
          <a [href]="segment.weblinkUrl" target="_blank">{{ segment.weblinkUrl }}</a>
        </div>
        @if (segment.weblinkDescription) {
          <div class="print-report-weblink-description">{{ segment.weblinkDescription }}</div>
        }
      </div>
    }
    @case ('form') {
      @let activity = segment.formActivity;
      <div class="print-report-form">
        <div class="print-report-form-header">
          <div class="medium bold">{{ activity.name }}</div>
        </div>

        @if (!activity.empty) {
          @switch (activity.type) {
            @case ('TEXT_FORM') {
              <div class="print-report-form-value">
                <ptl-resource-preview [previewContent]="[{ type: 'PARAGRAPH', content: activity.value }]"> </ptl-resource-preview>
              </div>
            }
            @case ('CHECKBOX_FORM') {
              <div class="print-report-form-value">
                <mat-icon aria-hidden="true" *ngIf="activity.value">check</mat-icon>
                <mat-icon aria-hidden="true" *ngIf="!activity.value">close</mat-icon>
              </div>
            }
            @case ('FILE_UPLOAD_FORM') {
              @if (activity.value) {
                <div class="print-report-form-value">
                  @for (file of activity.value; track $index) {
                    <div class="print-report-file-upload-item">
                      @if (file.permalink) {
                        <div class="print-report-permalink">
                          <a [href]="file.permalink" target="_blank">{{ file.permalink }}</a>
                        </div>
                      } @else {
                        <div class="file-name">{{ file.name }}</div>
                      }
                      @if (file.pdfUrl) {
                        <ptl-pdf-pages-renderer [pdfUrl]="file.pdfUrl" (loaded)="onPdfRendererLoaded()"> </ptl-pdf-pages-renderer>
                      }
                      @if (file.imageUrl) {
                        <img [src]="file.imageUrl" [alt]="file.name" class="print-report-file-image" />
                      }
                    </div>
                  }
                </div>
              }
            }
            @case ('QUIZ_FORM') {
              <div class="print-report-form-value">
                <ptl-quiz-results-report
                  [quiz]="activity.value.quiz"
                  [responses]="activity.value.responses"
                  [playlistDownloadReportView]="true"
                >
                </ptl-quiz-results-report>
              </div>
            }
            @case ('COLLECTOR_FORM') {
              @if (activity.value) {
                <div class="print-report-form-value">
                  @for (log of activity.value; track $index) {
                    <div class="collector-log">
                      <div *ngFor="let moment of log.moments; let j = index" class="collector-moment">
                        <h4>{{ moment.title ? moment.title : 'Moment ' + (j + 1) }}</h4>
                        <img *ngIf="moment.imageUrl" [src]="moment.imageUrl" alt="" class="collector-moment-image" />
                      </div>
                      <ptl-resource-preview *ngFor="let answer of log.answers" [previewContent]="[{ type: 'PARAGRAPH', content: answer }]">
                      </ptl-resource-preview>
                    </div>
                  }
                </div>
              }
            }
          }
        } @else {
          <div class="print-report-form-empty medium">
            <span [ngSwitch]="activity.type">
              <span *ngSwitchCase="'TEXT_FORM'">{{ 'translations.formNotSubmitted' | transloco }}</span>
              <span *ngSwitchCase="'CHECKBOX_FORM'">{{ 'translations.formNotSubmitted' | transloco }}</span>
              <span *ngSwitchCase="'FILE_UPLOAD_FORM'">{{ 'translations.fileNotUploaded' | transloco }}</span>
              <span *ngSwitchCase="'QUIZ_FORM'">{{ 'translations.quizNotTaken' | transloco }}</span>
              <span *ngSwitchCase="'COLLECTOR_FORM'">{{ 'translations.collector.label.notCompleted' | transloco }}</span>
            </span>
          </div>
        }

        <!-- Review status -->
        <div class="print-report-review" *ngIf="activity.review !== undefined && activity.review !== 'EMPTY'">
          <span class="review-label">{{ 'translations.global.label.review' | transloco }}:</span>
          <span class="review-status approved" *ngIf="activity.review === 'APPROVED'">
            {{ 'translations.approved' | transloco }}
          </span>
          <span class="review-status resubmit" *ngIf="activity.review === 'RESUBMIT'">
            {{ 'translations.resubmit' | transloco }}
          </span>
          <span *ngIf="activity.reviewDate"> {{ activity.reviewDate | dateFormat: 'DD MMMM yyyy' }}</span>
        </div>

        <!-- Comments -->
        <div class="print-report-comment" *ngFor="let comment of activity.comments">
          <span class="comment-label">
            {{ 'translations.comment' | transloco }}
            <span *ngIf="comment.index"> {{ comment.index }}</span
            >:
          </span>
          <span>{{ comment.note.message }}</span>
        </div>
      </div>
    }
  }
</ng-template>
